import type { ObjectDirective } from 'vue';

import { useServiceContainer } from '~/container';

interface SmartStickyPayload {
    offsetElementSelector?: string;
    offsetElementMarginTop?: number;
}

// References
const onScrollReference = new Map();

export const SmartSticky: ObjectDirective<HTMLImageElement, SmartStickyPayload> = {
    mounted(el, binding): void {
        const { ElementHelperService } = useServiceContainer();
        const { offsetElementSelector, offsetElementMarginTop } = binding.value;

        // Set references
        onScrollReference.set(el, onScroll);

        // Set listeners
        window.addEventListener('scroll', onScrollReference.get(el));

        let timer: NodeJS.Timeout | undefined = undefined;
        let previousPos: number = 0;
        let direction: 'up' | 'down' = 'down';

        function onScroll() {
            const pos = window.scrollY;

            if (pos > previousPos) {
                direction = 'down';
            } else {
                direction = 'up';
            }

            clearTimeout(timer);
            timer = setTimeout(() => {
                update();
                previousPos = pos < 0 ? 0 : pos;
            }, 100);
        }

        function update() {
            const elementHeight = ElementHelperService.height(el);
            const elementOffset = ElementHelperService.getOffsetTop(el);
            const oldOffset = el.style.paddingTop ? parseInt(el.style.paddingTop) : 0;
            const screenHeight = window.innerHeight;
            const scrollPosition = window.scrollY;
            const searchResultsHeight = ElementHelperService.height(
                document.querySelector('.et-search-results-page__results')!,
            );
            const topVisible = oldOffset + elementOffset > scrollPosition;
            const bottomVisible = elementOffset + elementHeight + oldOffset < screenHeight + scrollPosition;

            let offset = scrollPosition - elementOffset;

            if (typeof offsetElementSelector === 'string' && offsetElementSelector.length) {
                const searchQueryContainerHeight = ElementHelperService.outerHeight(
                    document.querySelector(offsetElementSelector)!,
                );
                const searchQueryContainerMarginTop = offsetElementMarginTop
                    ? parseInt(String(offsetElementMarginTop))
                    : 0;

                offset += searchQueryContainerHeight + searchQueryContainerMarginTop;
            }

            // 'sidebar' scrolling down
            if (direction === 'down' && !bottomVisible) {
                return false;
            }

            // 'sidebar' scrolling up
            if (direction === 'up' && !topVisible) {
                return false;
            }

            // fixed bottom position instead of fixed top
            if (direction === 'down' && elementHeight > screenHeight) {
                offset = scrollPosition + screenHeight - elementHeight - elementOffset;
            }

            // prevent infinite 'enlarging' of page height
            if (offset + elementHeight > searchResultsHeight) {
                offset = searchResultsHeight - elementHeight;
            }

            // never use negative padding
            if (offset < 0) {
                offset = 0;
            }

            el.style.paddingTop = `${parseInt(String(offset))}px`;
        }
    },
    beforeUnmount(el) {
        // Remove listeners
        window.removeEventListener('scroll', onScrollReference.get(el));

        // Remove references to clear potential memory leaks
        onScrollReference.delete(el);
    },
};
